// @flow
import React, { useEffect } from 'react'
import Head from '@components/common/head'
import { getSound } from '@data/api/sound'
import ErrorPage from '@pages/error'
import SoundContainer from '@pages/media/sound'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import useAuth from '@/hooks/auth'
import Loading from '@/components/common/loading'
import { trackSoundView } from '@/utils/algolia/insights'
import { PUBLICITY_PUBLIC } from '@/utils/constants'

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultSound?: any
  id: string
}

const swrOptions = {
  revalidateOnFocus: false,
  shouldRetryOnError: false,
}

async function fetcher(id: string) {
  const result = await getSound(id)
  if (result.error) {
    return Promise.reject(result.error)
  }
  return result
}

const Sound = ({ id, defaultSound = null }: Props) => {
  const { currentUser, isAuthLoading } = useAuth()
  const router = useRouter()
  const { data: sound, error, isLoading } = useSWR(
    id ? `/api/sound/${id}` : null,
    () => fetcher(`${id}`),
    defaultSound ? { fallbackData: defaultSound } : swrOptions,
  )

  const buy = !!router.query.buy

  useEffect(() => {
    if (isAuthLoading) {
      return
    }
    trackSoundView({ userId: currentUser?.id, soundId: id })
  }, [ currentUser?.id, id, isAuthLoading ])

  if (!sound && (isLoading || !router.query.p)) {
    return (
      <Loading />
    )
  }

  if (!sound || error) {
    return (
      <ErrorPage statusCode={404} />
    )
  }

  return (
    <>
      <Head
        title={`${sound.title} by ${sound.user.name}`}
        description={sound.description}
        url={`/sound/${sound.id}/${sound.slug}`}
        image={sound.coverart_crop_url}
        user={sound.user.social ? sound.user.social.twitter : ''}
        isPrivate={sound.status !== PUBLICITY_PUBLIC}
      />
      <SoundContainer sound={sound} buy={buy} />
    </>
  )
}

export const getStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
})

export const getStaticProps = async ({
  params,
}) => {
  const [ id ] = params.p
  // const { query: { id = '' } } = req
  if (!id) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  // fetch sound data here
  const sound = await getSound(id)

  // check if user is not exists after fetch
  if (sound.error) {
    return {
      props: { id },
      revalidate: 60,
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const breadcrumbs: any = [
    {
      label: sound.user.name,
      to: `/user/${sound.user.username}`,
    },
  ]
  if (sound.album) {
    breadcrumbs.push({
      label: sound.album.title,
      to: `/album/${sound.album.id}/${sound.album.slug}`,
    })
  }
  breadcrumbs.push({ label: sound.title })

  return {
    props: {
      id,
      defaultSound: { ...sound, id },
      breadcrumbs,
    },
    revalidate: 60,
  }
}

export default Sound
